import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText, Mail } from "lucide-react";
import { api } from "../../api/client";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";

// 忘記密碼。**公開頁面**,不套 AppShell 也不受登入守衛管轄 ——
// 會走到這一頁的人本來就進不去,被守衛擋住等於這個功能不存在。
//
// 這一頁只有一條不可妥協的規矩:**送出後的訊息與這個信箱有沒有帳號無關。**
// 後端刻意不論存不存在都回同一個 200,前端若在畫面上分成「已寄出」與
// 「查無此帳號」,那道防護就在最後一哩被自己拆掉了 —— 攻擊者拿一份 email
// 字典跑一輪,就得到一份客戶名單與員工名冊。
//
// 同理:送出後不顯示使用者剛打的信箱。那句話會停在螢幕上一段時間,
// 旁邊經過的人、截圖與螢幕分享都看得到。

/** 送出後的統一說法。與後端「一律回 200」是同一條規矩的兩半。 */
export const RESET_REQUESTED_NOTICE = "若這個信箱有帳號,重設信已寄出。請查看信箱。";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post<{ ok: boolean }>("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch {
      // 後端對這支端點一律回 200,走到這裡代表真的連不上或伺服器出錯 ——
      // 那與「這個信箱存不存在」無關,講出來不會洩漏任何東西。
      setError("無法連線到伺服器,請稍後再試。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg)] px-4 py-10">
      <div className="flex w-full max-w-sm flex-col gap-4 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-6">
        <div className="flex items-center gap-2">
          <FileText aria-hidden="true" size={20} className="text-[var(--accent)]" />
          <span className="text-sm font-semibold text-[var(--text)]">星采設計內部系統</span>
        </div>

        <h1 className="m-0 text-xl font-semibold text-[var(--text)]">忘記密碼</h1>

        {sent ? (
          <p
            role="status"
            className="m-0 rounded-[var(--radius)] border border-[var(--ok)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--text)]"
          >
            {RESET_REQUESTED_NOTICE}
          </p>
        ) : (
          <>
            <p className="m-0 text-sm text-[var(--text-2)]">
              填入登入用的 Email,我們會寄一封重設密碼的信給你。連結 1 小時後失效。
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <Input
                label="Email"
                type="email"
                name="email"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />

              {error ? (
                <p
                  role="alert"
                  className="m-0 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--danger)]"
                >
                  {error}
                </p>
              ) : null}

              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                icon={<Mail aria-hidden="true" size={16} />}
                className="mt-1"
              >
                寄出重設連結
              </Button>
            </form>
          </>
        )}

        <Link
          to="/login"
          className="inline-flex items-center gap-1 text-sm text-[var(--accent)] underline"
        >
          <ArrowLeft aria-hidden="true" size={14} />
          回登入
        </Link>
      </div>
    </div>
  );
}
